<script lang="ts" setup>
import CopilotAvatar from '@/components/Conversations/Avatar/CopilotAvatar.vue';
import DateUtils from '@/utils/date-utils';
import type { ConversationMessage } from '@/core/conversations/message/conversation-message.type';
import {
  type InterviewSchedulingMetadata,
  MessageMetadataType,
} from '@/core/conversations/message/conversation-message.type';
import { InterviewInitiationSource } from '@/core/shared/interview-scheduling/types/interview-scheduling-event.type';
import { computed } from 'vue';

const props = defineProps<{
  message: ConversationMessage;
}>();

const metadata = computed(() => props.message.metadata as InterviewSchedulingMetadata);

const formattedMessageDate = computed(() =>
  DateUtils.formatTimestamp(props.message.createdAt, 'MMMM D, YYYY h:mma'),
);

const isScheduled = computed(() => metadata.value.type === MessageMetadataType.INTERVIEW_SCHEDULED);

const aiRecruiterName = computed(() => metadata.value.aiRecruiterName);

const isManual = computed(
  () => metadata.value?.initiationSource === InterviewInitiationSource.MANUAL,
);

const recruiterName = computed(() => metadata.value.recruiterName);

const formattedScheduleDate = computed(() => {
  if (!metadata.value.scheduleDateTs) return '';
  return DateUtils.formatTimestamp(metadata.value.scheduleDateTs, 'MMMM D, YYYY, [at] h:mm A');
});

const getIndefiniteArticle = (word: string): 'a' | 'an' => {
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  const firstLetter = word.toLowerCase().charAt(0);
  return vowels.includes(firstLetter) ? 'an' : 'a';
};

const interviewTypeDisplay = computed(() => {
  const type = metadata.value.interviewTypeDisplay;

  return `${type} interview`;
});

const article = computed(() => getIndefiniteArticle(interviewTypeDisplay.value));

const jobTitle = computed(() => metadata.value.jobTitle);
</script>
<template>
  <div class="mb-3 mt-3 flex flex-row-reverse gap-1 pl-0 pr-0 font-sans">
    <CopilotAvatar :vertical-position="'center'" />

    <div class="mr-4">
      <div class="mb-1 whitespace-pre-wrap text-sm text-shade-900 [overflow-wrap:anywhere]">
        <template v-if="isScheduled">
          <strong>{{ aiRecruiterName }}</strong> scheduled {{ article }}
          <strong>{{ interviewTypeDisplay }}</strong>
          <template v-if="isManual">
            (initiated by <strong>{{ recruiterName }}</strong
            >)
          </template>
          — <strong>{{ formattedScheduleDate }}</strong> — <strong>{{ jobTitle }}</strong>
        </template>
        <template v-else>
          <strong>{{ aiRecruiterName }}</strong> sent an interview scheduling request
          <template v-if="isManual">
            (initiated by <strong>{{ recruiterName }}</strong
            >)
          </template>
          — <strong>{{ jobTitle }}</strong> — awaiting candidate response
        </template>
      </div>

      <div class="text-right text-xs text-shade-840">
        <span class="font-bold">{{ aiRecruiterName }}</span>
        <span class="ml-1 text-shade-800">{{ formattedMessageDate }}</span>
      </div>
    </div>
  </div>
</template>
